import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import API from "services/axios";
import authHeader from "services/auth-header";
import { GetNameWithTimeStamp } from "utils/UtilityFuncs";
import { setNotification } from "features/Notification/notificationSlice";
import BulkWProductDelete from "./BulkWProductDelete";

export default function UploadImagesButton({
  refreshFunc,
  selectedWorkOrder,
  allProducts,
}) {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]); // { file, preview, itemNo, product }
  const [uploadUnmatched, setUploadUnmatched] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleUploadImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const selected = [];
    const unmatched = [];

    files.forEach((file) => {
      const preview = URL.createObjectURL(file);
      const baseName = file.name.split(".")[0] || "";
      const match = baseName.match(/\d+/);
      const itemNo = match ? parseInt(match[0], 10) : null;

      const product =
        itemNo !== null
          ? (allProducts || []).find(
              (p) => p.sortId === itemNo || p.sortId === parseInt(itemNo),
            )
          : null;

      if (product) selected.push({ file, preview, itemNo, product });
      else unmatched.push({ file, preview, itemNo });
    });

    setUploadFiles((prev) => [...prev, ...selected]);
    setUploadUnmatched((prev) => [...prev, ...unmatched]);
    e.target.value = null;
  };

  const removeUploadFile = (index) => {
    setUploadFiles((prev) => {
      const copy = [...prev];
      const removed = copy.splice(index, 1);
      if (removed[0]?.preview) URL.revokeObjectURL(removed[0].preview);
      return copy;
    });
  };

  const uploadSelectedImages = async () => {
    if (!uploadFiles.length) return;
    setLoading(true);
    try {
      for (const item of uploadFiles) {
        const { file, product } = item;
        if (!product || !product.id) continue;

        const _formData = new FormData();
        const folder = `kms/wo/${selectedWorkOrder?.id}/products`;
        _formData.append("file", file);
        _formData.append("folder", folder);
        const fileName = GetNameWithTimeStamp(file.name);
        const productImage = `${folder}/${fileName}`;
        _formData.append("fileName", fileName);
        _formData.append(
          "data",
          JSON.stringify({ id: product.id, productImage }),
        );

        const { data } = await API.patch(`/wo-products`, _formData, {
          headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        });

        if (!(data && data.message && data.message.type === "success")) {
          dispatch(
            setNotification({
              message: `Failed to upload image for Item#${product.sortId}`,
              type: "error",
            }),
          );
        }
      }

      await refreshFunc();
      setUploadFiles([]);
      setUploadUnmatched([]);
      dispatch(
        setNotification({ message: "Images uploaded", type: "success" }),
      );
      setShowModal(false);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message || "Upload failed",
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    uploadFiles.forEach((u) => u.preview && URL.revokeObjectURL(u.preview));
    uploadUnmatched.forEach((u) => u.preview && URL.revokeObjectURL(u.preview));
    setUploadFiles([]);
    setUploadUnmatched([]);
    setSearchQuery("");
    setShowModal(false);
  };

  const linkUnmatchedToProduct = (unmatchedIndex, productId) => {
    if (!productId) return;
    setUploadUnmatched((prevUnmatched) => {
      const copy = [...prevUnmatched];
      const removed = copy.splice(unmatchedIndex, 1);
      const unmatchedItem = removed[0];
      if (!unmatchedItem) return prevUnmatched;

      const prod = (allProducts || []).find(
        (p) => String(p.id) === String(productId),
      );
      if (!prod) return copy;

      setUploadFiles((prev) => [
        ...prev,
        {
          file: unmatchedItem.file,
          preview: unmatchedItem.preview,
          itemNo: prod.sortId || unmatchedItem.itemNo,
          product: prod,
        },
      ]);

      return copy;
    });
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        type="button"
        className="btn btn-sm py-0 w-100 text-start ps-0"
        disabled={loading}
      >
        Import Images
      </button>

      <Modal size="lg" show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload Images</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div>
            <div className="mb-2 text-muted">
              Name files with item no, e.g. 1.png
            </div>
            <input
              type="file"
              multiple
              accept="image/png, image/jpeg"
              className="form-control my-2"
              onChange={handleUploadImageSelect}
            />

            {uploadFiles.length > 0 && (
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <strong>Matched Files</strong>
                    <div className="text-muted small">
                      Showing files matched to products by item no
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    <input
                      type="search"
                      placeholder="Search by item, style, color, description, team"
                      className="form-control form-control-sm me-2"
                      style={{ minWidth: 220 }}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>Item No</th>
                        <th>Preview</th>
                        <th>Style</th>
                        <th>Color</th>
                        <th>Description</th>
                        <th>Team</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadFiles
                        .filter((m) => {
                          if (!searchQuery) return true;
                          const q = searchQuery.toLowerCase();
                          const prod = m.product || {};
                          return (
                            String(m.itemNo).includes(q) ||
                            (prod.productStyle || "")
                              .toLowerCase()
                              .includes(q) ||
                            (prod.productColor || "")
                              .toLowerCase()
                              .includes(q) ||
                            (prod.productDescription || "")
                              .toLowerCase()
                              .includes(q) ||
                            (prod.teamName || "").toLowerCase().includes(q) ||
                            (m.file?.name || "").toLowerCase().includes(q)
                          );
                        })
                        .map((m, i) => (
                          <tr key={i}>
                            <td className="align-middle">{m.itemNo}</td>
                            <td style={{ width: 120 }}>
                              <img
                                src={m.preview}
                                alt={`item-${m.itemNo}`}
                                style={{
                                  width: "100%",
                                  height: 60,
                                  objectFit: "cover",
                                }}
                              />
                            </td>
                            <td className="align-middle">
                              {m.product?.productStyle}
                            </td>
                            <td className="align-middle">
                              {m.product?.productColor}
                            </td>
                            <td className="align-middle">
                              {m.product?.productDescription}
                            </td>
                            <td className="align-middle">
                              {m.product?.teamName}
                            </td>
                            <td className="align-middle">
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => removeUploadFile(i)}
                                type="button"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* <div className="mt-3">
                  <button
                    onClick={uploadSelectedImages}
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? "Uploading..." : "Upload Selected Images"}
                  </button>
                </div> */}
              </div>
            )}

            {uploadUnmatched.length > 0 && (
              <div className="mt-2">
                <div className="fw-bold mb-2">Unmatched files</div>
                <div className="table-responsive">
                  <table className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>Preview</th>
                        <th>Filename</th>
                        <th>Item#</th>
                        <th>Link To Product</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadUnmatched.map((u, i) => {
                        const alreadyMatchedIds = (uploadFiles || []).map((m) =>
                          String(m.product?.id),
                        );

                        const selectedUnmatchedIds = (uploadUnmatched || [])
                          .map((x, idx) =>
                            idx === i ? null : x?._selectedProductId,
                          )
                          .filter(Boolean)
                          .map(String);

                        const remainingProducts = (allProducts || []).filter(
                          (p) =>
                            !alreadyMatchedIds.includes(String(p.id)) &&
                            !selectedUnmatchedIds.includes(String(p.id)),
                        );

                        return (
                          <tr key={i}>
                            <td style={{ width: 120 }}>
                              <img
                                src={u.preview}
                                alt={`unmatched-${i}`}
                                style={{
                                  width: "100%",
                                  height: 60,
                                  objectFit: "cover",
                                }}
                              />
                            </td>
                            <td className="align-middle">{u.file?.name}</td>
                            <td className="align-middle">{u.itemNo || "-"}</td>
                            <td
                              className="align-middle"
                              style={{ minWidth: 220 }}
                            >
                              <div className="d-flex">
                                <select
                                  className="form-select form-select-sm me-2"
                                  defaultValue=""
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (!val) return;
                                    linkUnmatchedToProduct(i, val);
                                  }}
                                >
                                  <option value="">
                                    {" "}
                                    -- select product --{" "}
                                  </option>
                                  {remainingProducts.map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {`${p.productType ? p.productType + " | " : ""}${p.sortId} — ${p.productStyle || ""} ${p.productColor || ""}${p.productDescription ? " | " + p.productDescription : ""}${p.teamName ? " | " + p.teamName : ""}`}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button className="btn btn-outline-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={uploadSelectedImages}
            disabled={loading || uploadFiles.length === 0}
          >
            {loading ? "Uploading..." : "Upload Selected Images"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
